import { Dispatch, FC, SetStateAction, useMemo } from 'react';
import Select from 'react-select';

import { Box, Button, TextInput } from '@qwealth/qcore';
import { Partner, selectPartners, selectUser, toOptions } from '@qwealth/qdata';

import { useAppSelector } from 'data/store';
import { selectTradeRequests } from 'data/selectors/tradeRequests';
import {
  TRADE_REQUEST_STATUSES,
  TRADE_REQUEST_TAGS,
  TradeRequestFilters,
  TradeRequestStatus,
} from 'data/models/TradeRequest';
import { initialFilters } from 'data/helpers/tradeRequests';
import { baseSelectStyles } from 'components/common/BaseSelect';

interface Props {
  searchQuery: string;
  setSearchQuery: Dispatch<SetStateAction<string>>;
  mutableFilters: TradeRequestFilters;
  setMutableFilters: Dispatch<SetStateAction<TradeRequestFilters>>;
}

const TradeRequestsHeader: FC<Props> = ({ searchQuery, setSearchQuery, mutableFilters, setMutableFilters }): JSX.Element => {
  const tradeRequests = useAppSelector(selectTradeRequests);
  const partnerList: Partner[] = useAppSelector(selectPartners);
  const { firstName, lastName } = useAppSelector(selectUser);
  const myDisplayName = `${firstName} ${lastName}`;

  const partnerOptions = useMemo(
    () => toOptions<string>(partnerList.map(p => p.partnerName)),
    [partnerList],
  );

  const assigneeOptions = useMemo(() => {
    const unique = Array.from(new Set(tradeRequests.map(r => r.assignedTo).filter(Boolean))) as string[];
    return [
      { value: myDisplayName, label: 'Assigned to Me' },
      ...toOptions<string>(unique),
    ];
  }, [tradeRequests, myDisplayName]);

  const handleResetFilters = () => {
    setMutableFilters(initialFilters);
    setSearchQuery('');
  };

  return (
    <Box display="flex" flexDirection="column" gap="default" marginBottom="large">
      <Box display="flex" flexDirection="column">
        <TextInput
          placeholder="Search by Household, QID, Partner, IA Code, Assignee"
          data-testid="trade-request-search-input"
          onChange={e => setSearchQuery(e.target.value ?? '')}
          value={searchQuery}
        />
      </Box>

      <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(250px, 1fr))" gap="default">
        <Box display="flex" flexDirection="column">
          <Box textStyle="formLabel">Status</Box>
          <Select
            placeholder="Select Status"
            styles={baseSelectStyles()}
            value={mutableFilters.status ? toOptions<TradeRequestStatus>([mutableFilters.status]) : null}
            onChange={options => setMutableFilters(prev => ({ ...prev, status: options ? options.value : null }))}
            options={toOptions<TradeRequestStatus>([...TRADE_REQUEST_STATUSES])}
            isClearable
          />
        </Box>

        <Box display="flex" flexDirection="column">
          <Box textStyle="formLabel">Partner</Box>
          <Select
            placeholder="Select Partner"
            styles={baseSelectStyles()}
            value={mutableFilters.partnerName ? toOptions<string>([mutableFilters.partnerName]) : null}
            onChange={options => setMutableFilters(prev => ({ ...prev, partnerName: options ? options.value : null }))}
            options={partnerOptions}
            isClearable
          />
        </Box>

        <Box display="flex" flexDirection="column">
          <Box textStyle="formLabel">Tag</Box>
          <Select
            placeholder="Select Tag"
            styles={baseSelectStyles()}
            value={mutableFilters.tag ? toOptions<string>([mutableFilters.tag]) : null}
            onChange={options => setMutableFilters(prev => ({ ...prev, tag: options ? options.value : null }))}
            options={toOptions<string>([...TRADE_REQUEST_TAGS])}
            isClearable
          />
        </Box>

        <Box display="flex" flexDirection="column">
          <Box textStyle="formLabel">Assignee</Box>
          <Select
            placeholder="Select Assignee"
            styles={baseSelectStyles()}
            value={mutableFilters.assignedTo ? toOptions<string>([mutableFilters.assignedTo]) : null}
            onChange={options => setMutableFilters(prev => ({ ...prev, assignedTo: options ? options.value : null }))}
            options={assigneeOptions}
            isClearable
          />
        </Box>
      </Box>

      <Box display="flex" flexDirection="column" gap="small">
        <Box>
          Showing <strong>{tradeRequests.length} trade requests</strong> for your firm.
        </Box>
        <Box fontStyle="italic">
          <strong>Note:</strong> If you do not see requests you expect, adjust the filters above or click Reset Filters.
        </Box>
        <Box display="flex" justifyContent="flex-start" alignItems="flex-end">
          <Button onClick={handleResetFilters}>Reset Filters</Button>
        </Box>
      </Box>
    </Box>
  );
};

export default TradeRequestsHeader;
